// Phase 6 — Add Video Lesson dialog (Device / Google Drive / OneDrive / URL)
// Iteration 3:
//   • Chunked / resumable device uploads (5 MB per chunk, resume on network drop)
//   • Real Google Drive Picker wired via gapi.load('picker')
//   • Real OneDrive Picker wired via https://js.live.net/v7.2/OneDrive.js
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, Loader2, HardDrive, Cloud, Link2, CheckCircle2, XCircle, PauseCircle, PlayCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError, API_BASE } from "@/lib/api";

const TABS = [
  { k: "device",   label: "Upload from Device", icon: HardDrive },
  { k: "drive",    label: "Google Drive",       icon: Cloud },
  { k: "onedrive", label: "OneDrive",           icon: Cloud },
  { k: "url",      label: "Video URL",          icon: Link2 },
];

const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

// ---- Dynamic script loader (idempotent) -------------------------------------
const loadedScripts = new Set();
function loadScript(src) {
  if (loadedScripts.has(src)) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) { loadedScripts.add(src); return resolve(); }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => { loadedScripts.add(src); resolve(); };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

// =============================================================================
export function AddVideoDialog({ open, onOpenChange, lessonId, onSuccess }) {
  const [tab, setTab] = useState("device");
  const [title, setTitle] = useState("");
  const _setTitle = (v) => { titleTouchedRef.current = true; setTitle(v); };
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [driveUrl, setDriveUrl] = useState("");
  const [oneUrl, setOneUrl] = useState("");
  const [extUrl, setExtUrl] = useState("");
  const [progress, setProgress] = useState(0);           // 0..100 — device upload progress
  const [busy, setBusy] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [statusText, setStatusText] = useState("");
  const [paused, setPaused] = useState(false);
  // YouTube auto-detect: {duration_seconds, title, thumbnail_url, loading}
  const [ytInfo, setYtInfo] = useState(null);
  const ytFetchTimer = useRef(null);
  const titleTouchedRef = useRef(false);
  const uploadIdRef = useRef(null);
  const abortRef = useRef({ aborted: false, paused: false });

  useEffect(() => {
    if (!open) {
      setTab("device"); setTitle(""); setDesc(""); setFile(null);
      setDriveUrl(""); setOneUrl(""); setExtUrl("");
      setProgress(0); setBusy(false); setVideoId(null); setStatusText("");
      setPaused(false); setYtInfo(null); titleTouchedRef.current = false;
      uploadIdRef.current = null;
      abortRef.current = { aborted: false, paused: false };
    }
  }, [open]);

  // Debounced YouTube info fetch — kicks in when the URL field settles
  // on something that looks like a YouTube link. We keep this on the
  // frontend so faculty see the detected duration + title as a badge
  // BEFORE they hit Save; server-side we also re-fetch as a safety net
  // (see external_url endpoint) so a flaky browser fetch never leaves
  // the DB with `duration_seconds=0` for a real YouTube video.
  useEffect(() => {
    if (ytFetchTimer.current) { clearTimeout(ytFetchTimer.current); ytFetchTimer.current = null; }
    setYtInfo(null);
    const url = (extUrl || "").trim();
    if (!url || tab !== "url") return undefined;
    const looksYouTube = /youtu\.?be/.test(url);
    if (!looksYouTube) return undefined;
    ytFetchTimer.current = setTimeout(async () => {
      setYtInfo({ loading: true });
      try {
        const { data } = await api.get("/admin/video-tools/youtube-info", { params: { url } });
        const d = data?.data || {};
        setYtInfo({ ...d, loading: false });
        // Auto-fill title only if faculty hasn't typed one yet (so we
        // never clobber a manually-typed course-specific title).
        if (d.title && !titleTouchedRef.current && !title.trim()) setTitle(d.title);
      } catch {
        setYtInfo({ loading: false, error: true });
      }
    }, 500);
    return () => { if (ytFetchTimer.current) { clearTimeout(ytFetchTimer.current); ytFetchTimer.current = null; } };
  }, [extUrl, tab, title]);

  // Poll status while a video is processing
  useEffect(() => {
    if (!videoId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/lesson-videos/${videoId}/status`);
        const v = data.data.video;
        if (v.upload_status === "ready") {
          setStatusText("Video is ready for secure playback.");
          onSuccess?.(v);
          toast.success("Video is ready for secure playback.");
          setTimeout(() => onOpenChange(false), 800);
          return;
        }
        if (v.upload_status === "failed" || v.compression_status === "failed") {
          setStatusText(`Failed: ${v.processing_error || "Video processing failed."}`);
          // Surface the SPECIFIC backend error (e.g. 'This OneDrive link isn't
          // publicly accessible…') instead of a generic toast so users know
          // exactly what to fix.
          toast.error(v.processing_error || "Video processing failed. Please retry or upload another file.", { duration: 8000 });
          return;
        }
        setStatusText(({
          uploading: "Uploading…",
          uploaded: "Uploaded — optimization is in progress.",
        }[v.upload_status] || "Processing…") + ({
          queued: " (queued)",
          processing: " (compressing…)",
        }[v.compression_status] || ""));
        if (!stopped) setTimeout(poll, 2000);
      } catch {
        if (!stopped) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, [videoId, onOpenChange, onSuccess]);

  // ---- Picker config + Google gapi/token client ---------------------------
  const [pickerCfg, setPickerCfg] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/pickers/config");
        setPickerCfg(data.data);
      } catch { /* silent — falls back to URL paste */ }
    })();
  }, []);

  // Detect obvious placeholder / dummy values that will fail silently at the
  // Google/Microsoft OAuth layer. Real Google client IDs end in
  // `.apps.googleusercontent.com` and are long; real Microsoft app ids are GUIDs.
  const looksLikePlaceholder = (v) => {
    if (!v || typeof v !== "string") return true;
    const s = v.trim().toLowerCase();
    if (!s) return true;
    if (s.length < 20) return true;
    return ["test", "dummy", "example", "placeholder", "aizatest"].some((tok) => s.includes(tok));
  };
  const googleReady = !!pickerCfg
    && !looksLikePlaceholder(pickerCfg.google_client_id)
    && !looksLikePlaceholder(pickerCfg.google_api_key);
  const onedriveReady = !!pickerCfg && !looksLikePlaceholder(pickerCfg.onedrive_client_id);

  // ---- Chunked upload with resume support --------------------------------
  const uploadDeviceChunked = async () => {
    if (!file) throw new Error("Choose a video file.");
    setBusy(true); setPaused(false); setProgress(0);
    abortRef.current = { aborted: false, paused: false };

    // 1. init session
    const { data: initResp } = await api.post("/admin/videos/upload-init", {
      lesson_id: lessonId,
      video_title: title,
      video_description: desc,
      file_name: file.name,
      file_size: file.size,
    });
    const uploadId = initResp.data.upload_id;
    const chunkSize = initResp.data.chunk_size || DEFAULT_CHUNK_SIZE;
    uploadIdRef.current = uploadId;

    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));

    // 2. resume-aware upload loop
    let received = new Set();
    const refreshServerState = async () => {
      try {
        const { data } = await api.get(`/admin/videos/upload-status/${uploadId}`);
        received = new Set(data.data.received_chunks || []);
      } catch { /* ignore */ }
    };

    for (let i = 0; i < totalChunks; i++) {
      if (abortRef.current.aborted) throw new Error("Upload aborted.");
      // Pause loop
      while (abortRef.current.paused && !abortRef.current.aborted) {
        await new Promise((r) => setTimeout(r, 400));
      }
      if (received.has(i)) {
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
        continue;
      }
      const start = i * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const blob = file.slice(start, end);

      // Retry current chunk up to 3 times
      let attempt = 0; let uploaded = false; let lastErr = null;
      while (attempt < 3 && !uploaded && !abortRef.current.aborted) {
        try {
          const resp = await fetch(
            `${API_BASE}/admin/videos/upload-chunk/${uploadId}?index=${i}`,
            {
              method: "PUT",
              body: blob,
              credentials: "include",
              headers: { "Content-Type": "application/octet-stream" },
            },
          );
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          uploaded = true;
        } catch (e) {
          lastErr = e; attempt += 1;
          await new Promise((r) => setTimeout(r, 800 * attempt));
          await refreshServerState(); // in case server actually got the chunk
          if (received.has(i)) { uploaded = true; break; }
        }
      }
      if (!uploaded) throw new Error(`Chunk ${i + 1}/${totalChunks} failed after retries: ${lastErr?.message || "network error"}`);

      setProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    // 3. complete
    const { data: doneResp } = await api.post(`/admin/videos/upload-complete/${uploadId}`);
    return doneResp.data;
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Please enter a lesson title.");
    setBusy(true); setStatusText("");
    try {
      if (tab === "device") {
        const data = await uploadDeviceChunked();
        toast.success("Upload complete. Optimization is in progress.");
        setVideoId(data.video_id);
        setStatusText("Upload complete — optimization is in progress.");
      } else if (tab === "drive") {
        if (!driveUrl.trim()) throw new Error("Paste a Google Drive share URL.");
        const { data } = await api.post(`/admin/lessons/${lessonId}/videos/import-google-drive`, {
          lesson_id: lessonId, video_title: title, video_description: desc, source_url: driveUrl.trim(),
        });
        toast.success(data.message);
        setVideoId(data.data.video_id);
      } else if (tab === "onedrive") {
        if (!oneUrl.trim()) throw new Error("Paste a OneDrive share URL.");
        const { data } = await api.post(`/admin/lessons/${lessonId}/videos/import-onedrive`, {
          lesson_id: lessonId, video_title: title, video_description: desc, source_url: oneUrl.trim(),
        });
        toast.success(data.message);
        setVideoId(data.data.video_id);
      } else if (tab === "url") {
        if (!extUrl.trim()) throw new Error("Paste a YouTube / Vimeo / video URL.");
        const { data } = await api.post(`/admin/lessons/${lessonId}/videos/external-url`, {
          lesson_id: lessonId, video_title: title, video_description: desc,
          external_url: extUrl.trim(),
          // Send the client-fetched duration so completion maths starts
          // on the right footing. Server also re-fetches as a fallback.
          duration_seconds: Number(ytInfo?.duration_seconds || 0) || 0,
        });
        // Post-save confirmation — surface the detected duration so
        // faculty spot a mistake at the moment of entry (a red flag if
        // it disagrees with what they expected). We prefer the server
        // response (authoritative — it includes any server-side
        // fallback fetch when the client didn't send one), and fall
        // back to what the client had detected during the paste.
        const savedDur = Number(data.data?.duration_seconds || ytInfo?.duration_seconds || 0) || 0;
        if (savedDur > 0) {
          const mm = Math.floor(savedDur / 60);
          const ss = String(savedDur % 60).padStart(2, "0");
          toast.success(`Saved with duration ${mm}:${ss} (detected from YouTube).`);
        } else {
          toast.success(data.message || "External video link saved. Duration will be auto-detected on first play.");
        }
        onSuccess?.(data.data);
        setTimeout(() => onOpenChange(false), 400);
      }
    } catch (err) {
      toast.error(err.message || formatApiError(err));
      setStatusText(`Failed: ${err.message || "unknown error"}`);
    } finally { setBusy(false); }
  };

  // ---- Pause / Resume / Abort --------------------------------------------
  const togglePause = () => {
    abortRef.current.paused = !abortRef.current.paused;
    setPaused(abortRef.current.paused);
  };

  const abortUpload = async () => {
    abortRef.current.aborted = true;
    if (uploadIdRef.current) {
      try { await api.delete(`/admin/videos/upload-abort/${uploadIdRef.current}`); } catch { /* ignore */ }
    }
    setBusy(false); setProgress(0); setStatusText("Upload cancelled.");
  };

  // ---- Google Drive Picker ------------------------------------------------
  const openGooglePicker = async () => {
    if (!googleReady) {
      return toast.error("Google Drive Picker isn't configured with real credentials. Ask a Super Admin to add a valid Google OAuth Client ID + API Key on Social Integrations (or paste a Google Drive share URL below).");
    }
    const cid = pickerCfg?.google_client_id;
    const key = pickerCfg?.google_api_key;
    const appId = pickerCfg?.google_app_id;
    try {
      await loadScript("https://apis.google.com/js/api.js");
      await loadScript("https://accounts.google.com/gsi/client");
      // Load the Picker module
      await new Promise((resolve) => window.gapi.load("picker", { callback: resolve }));

      // OAuth token via GIS
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: cid,
        scope: "https://www.googleapis.com/auth/drive.readonly",
        callback: (resp) => {
          if (resp.error) {
            toast.error(`Google auth failed: ${resp.error}`);
            return;
          }
          const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS_VIDEOS)
            .setIncludeFolders(true)
            .setSelectFolderEnabled(false);
          const builder = new window.google.picker.PickerBuilder()
            .addView(view)
            .setOAuthToken(resp.access_token)
            .setDeveloperKey(key)
            .setCallback((res) => {
              if (res.action === window.google.picker.Action.PICKED) {
                const doc = res.docs && res.docs[0];
                if (doc?.id) {
                  const url = `https://drive.google.com/file/d/${doc.id}/view`;
                  setDriveUrl(url);
                  if (!title.trim() && doc.name) setTitle(doc.name.replace(/\.[a-z0-9]+$/i, ""));
                  toast.success(`Selected: ${doc.name || doc.id}`);
                }
              }
            });
          if (appId) builder.setAppId(appId);
          builder.build().setVisible(true);
        },
      });
      tokenClient.requestAccessToken();
    } catch (e) {
      toast.error(`Picker failed to open: ${e.message || e}`);
    }
  };

  // ---- OneDrive Picker ----------------------------------------------------
  const openOneDrivePicker = async () => {
    if (!onedriveReady) {
      return toast.error("OneDrive Picker isn't configured with real credentials. Ask a Super Admin to add a valid Microsoft App Client ID on Social Integrations (or paste a OneDrive share URL below).");
    }
    const cid = pickerCfg?.onedrive_client_id;
    try {
      await loadScript("https://js.live.net/v7.2/OneDrive.js");
      const odOptions = {
        clientId: cid,
        action: "share",
        multiSelect: false,
        openInNewWindow: true,
        advanced: {
          filter: ".mp4,.mov,.webm,.mkv,.m4v,.avi",
          redirectUri: pickerCfg?.onedrive_redirect_uri || window.location.origin,
        },
        success: (files) => {
          const item = files?.value?.[0] || files?.[0];
          const link = item?.webUrl || item?.["@microsoft.graph.downloadUrl"] || item?.link?.webUrl;
          if (link) {
            setOneUrl(link);
            if (!title.trim() && item.name) setTitle(item.name.replace(/\.[a-z0-9]+$/i, ""));
            toast.success(`Selected: ${item.name || "OneDrive file"}`);
          } else {
            toast.error("OneDrive Picker returned no usable file link.");
          }
        },
        cancel: () => { /* user closed */ },
        error: (err) => toast.error(`OneDrive Picker error: ${err?.message || err}`),
      };
      window.OneDrive.open(odOptions);
    } catch (e) {
      toast.error(`Picker failed to open: ${e.message || e}`);
    }
  };

  const canSubmit = title.trim() && !busy && !videoId && (
    (tab === "device" && file) ||
    (tab === "drive" && driveUrl.trim()) ||
    (tab === "onedrive" && oneUrl.trim()) ||
    (tab === "url" && extUrl.trim())
  );

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-video-dialog">
        <DialogHeader>
          <DialogTitle>Add Video Lesson</DialogTitle>
          <p className="text-xs text-slate-500">
            Upload from device, Google Drive, OneDrive, or paste a secure video link. Videos are optimized (H.264 720p) for smooth LMS playback.
          </p>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="video-source-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                type="button"
                onClick={() => setTab(t.k)}
                disabled={busy || videoId}
                data-testid={`video-tab-${t.k}`}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
                  active ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="col-span-2 space-y-1">
            <Label>Lesson Title *</Label>
            <Input value={title} onChange={(e) => _setTitle(e.target.value)}
              placeholder="e.g. Introduction to Basic Life Support" data-testid="video-title" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Lesson Description</Label>
            <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)}
              placeholder="Short description shown to learners" />
          </div>

          {/* Tab-specific */}
          {tab === "device" && (
            <label className="col-span-2 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center cursor-pointer hover:border-[#101876]/60 hover:bg-slate-100 transition"
              data-testid="video-device-dropzone">
              <UploadCloud className="h-8 w-8 text-slate-400" />
              <div className="text-sm font-semibold text-slate-700">
                {file ? file.name : "Click to browse or drag a video file"}
              </div>
              <div className="text-[10px] text-slate-500">
                MP4, MOV, WebM, AVI, MKV · up to 1 GB · chunked & resumable
              </div>
              {file && (
                <div className="text-[10px] font-mono text-slate-500">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </div>
              )}
              <input type="file" accept="video/*"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={busy || videoId}
                data-testid="video-file-input" />
            </label>
          )}
          {tab === "drive" && (
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-2">
                <button type="button" onClick={openGooglePicker}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="video-drive-picker">
                  <Cloud className="h-3.5 w-3.5" /> Pick from Google Drive
                </button>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${googleReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  data-testid="video-drive-status">
                  {googleReady ? "Ready" : "Not configured"}
                </span>
              </div>
              {!googleReady && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800" data-testid="video-drive-config-hint">
                  <b>Picker not usable yet.</b> Google Drive OAuth credentials aren&apos;t set up. Either
                  paste a share URL below (works today) or configure the Picker in{" "}
                  <a href="/app/super-admin/integrations/social" target="_blank" rel="noreferrer" className="font-bold underline">
                    Super Admin → Social Integrations
                  </a>
                  . Get keys from{" "}
                  <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="underline">
                    console.cloud.google.com/apis/credentials
                  </a>.
                </div>
              )}
              <Label>Or paste Google Drive Share URL</Label>
              <Input value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)}
                placeholder="https://drive.google.com/file/d/<file-id>/view"
                data-testid="video-drive-url" />
              <p className="text-[10px] text-slate-500">
                Make the file&apos;s sharing set to <b>&quot;Anyone with the link&quot;</b>. We&apos;ll import a copy into LMS storage, compress it, and revoke direct access.
              </p>
            </div>
          )}
          {tab === "onedrive" && (
            <div className="col-span-2 space-y-2">
              <div className="flex items-center gap-2">
                <button type="button" onClick={openOneDrivePicker}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="video-onedrive-picker">
                  <Cloud className="h-3.5 w-3.5" /> Pick from OneDrive
                </button>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${onedriveReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  data-testid="video-onedrive-status">
                  {onedriveReady ? "Ready" : "Not configured"}
                </span>
              </div>
              {!onedriveReady && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-[10px] text-amber-800" data-testid="video-onedrive-config-hint">
                  <b>Picker not usable yet.</b> OneDrive OAuth credentials aren&apos;t set up. Either
                  paste a share URL below (works today) or configure the Picker in{" "}
                  <a href="/app/super-admin/integrations/social" target="_blank" rel="noreferrer" className="font-bold underline">
                    Super Admin → Social Integrations
                  </a>
                  . Get keys from{" "}
                  <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer" className="underline">
                    Azure Portal → App registrations
                  </a>.
                </div>
              )}
              {onedriveReady && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[10px] text-slate-700">
                  <b>Share-link tip.</b> Corporate OneDrive files (URLs containing{" "}
                  <code>-my.sharepoint.com</code>) usually require your organisation&apos;s login to open. In OneDrive
                  click <b>Share → Anyone with the link → Can view</b> before pasting the URL — otherwise the download
                  will fail with <b>403 Forbidden</b>.
                </div>
              )}
              {!onedriveReady && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-[10px] text-slate-700">
                  <b>Share-link tip.</b> Set access to <b>&quot;Anyone with the link&quot;</b>. Corporate OneDrive URLs
                  (containing <code>-my.sharepoint.com</code>) need this explicitly, otherwise the download will 403.
                </div>
              )}
              <Label>Or paste OneDrive Share URL</Label>
              <Input value={oneUrl} onChange={(e) => setOneUrl(e.target.value)}
                placeholder="https://1drv.ms/… or https://onedrive.live.com/…"
                data-testid="video-onedrive-url" />
              <p className="text-[10px] text-slate-500">
                Set share access to <b>Anyone with the link</b>. We&apos;ll import + compress into LMS storage.
              </p>
            </div>
          )}
          {tab === "url" && (
            <div className="col-span-2 space-y-1">
              <Label>Video URL (YouTube / Vimeo)</Label>
              <Input value={extUrl} onChange={(e) => setExtUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=…"
                data-testid="video-external-url" />
              {/* Live YouTube auto-detect badge — appears as soon as a
                  valid URL is typed. Reads: "Detected 14:24 · Video title" */}
              {ytInfo && (
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900" data-testid="yt-autodetect">
                  {ytInfo.loading ? (
                    <><Loader2 className="mt-0.5 h-3 w-3 animate-spin" /> Detecting YouTube duration…</>
                  ) : ytInfo.error ? (
                    <><XCircle className="mt-0.5 h-3 w-3 text-rose-600" /> <span className="text-rose-700">Couldn&apos;t auto-detect duration. Faculty must save without it — server will re-fetch on first learner heartbeat.</span></>
                  ) : ytInfo.duration_seconds ? (
                    <><CheckCircle2 className="mt-0.5 h-3 w-3 text-emerald-700" />
                      <div className="min-w-0">
                        <div className="font-semibold" data-testid="yt-autodetect-duration">
                          Detected duration: {Math.floor(ytInfo.duration_seconds / 60)}:{String(ytInfo.duration_seconds % 60).padStart(2, "0")}
                          <span className="ml-2 font-normal text-emerald-700/70">({ytInfo.duration_seconds}s)</span>
                        </div>
                        {ytInfo.title && <div className="truncate text-[10px] text-emerald-800/80" title={ytInfo.title}>{ytInfo.title}</div>}
                      </div></>
                  ) : (
                    <><XCircle className="mt-0.5 h-3 w-3 text-amber-600" /> <span className="text-amber-800">URL detected but no duration returned. Server will retry when the first learner opens the video.</span></>
                  )}
                </div>
              )}
              <p className="text-[10px] text-rose-600">
                ⚠ External video availability depends on the source platform. For secure LMS playback, upload or import the video into Steward Learning Cloud instead.
              </p>
            </div>
          )}
        </div>

        {/* Chunked upload progress */}
        {tab === "device" && busy && !videoId && (
          <div className="mt-4 space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid="chunk-upload-panel">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-slate-700">
                {paused ? "Paused" : "Uploading in chunks…"} · {progress}%
              </span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={togglePause}
                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                  data-testid="chunk-upload-pause">
                  {paused ? <PlayCircle className="h-3 w-3" /> : <PauseCircle className="h-3 w-3" />}
                  {paused ? "Resume" : "Pause"}
                </button>
                <button type="button" onClick={abortUpload}
                  className="inline-flex items-center gap-1 rounded border border-rose-200 bg-white px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"
                  data-testid="chunk-upload-abort">
                  Cancel
                </button>
              </div>
            </div>
            <div className="h-2 w-full overflow-hidden rounded bg-slate-200">
              <div className="h-full bg-[#101876] transition-all"
                style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[10px] text-slate-500">
              Uploads resume automatically if your connection drops — chunks already accepted by the server will be skipped.
            </p>
          </div>
        )}

        {/* Post-upload status */}
        {((videoId || (statusText && (!busy || tab !== "device")))) && (
          <div className="mt-4 space-y-2 rounded-lg border border-slate-100 bg-slate-50 p-3">
            <div className="flex items-center gap-2 text-xs">
              {videoId && !statusText.startsWith("Failed") && !statusText.startsWith("Video is ready") ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#101876]" />
              ) : statusText.startsWith("Video is ready") ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              ) : statusText.startsWith("Failed") ? (
                <XCircle className="h-3.5 w-3.5 text-rose-600" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#101876]" />
              )}
              <span className="font-semibold text-slate-700" data-testid="video-status-text">
                {statusText || "Working…"}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-1 text-[10px] font-semibold uppercase">
              {["Upload", "Compression", "Thumbnail", "Ready", "Publish"].map((step, i) => {
                const done = statusText.startsWith("Video is ready") ? true
                  : statusText.includes("optimization") ? i < 1
                  : statusText.includes("compressing") ? i < 2
                  : false;
                return (
                  <div key={step} className={`rounded px-1.5 py-1 text-center ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                    {step}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70">
            {videoId ? "Close" : "Cancel"}
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-60"
            data-testid="video-submit-btn">
            {busy || videoId ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            {tab === "device" ? "Upload & Optimize" : tab === "url" ? "Save Link" : "Import & Optimize"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
